import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { stringInLocale } from '../lib/use-i18n.js';
import { switchPathnameLocale } from '../lib/locale-routing.js';
import { suggestedLocale } from '../lib/locale-suggestion.js';

/**
 * Soft, dismissible locale-suggestion banner (follow-up to PR #72 / ADR-0024).
 *
 * Restores the [[feedback-locale-detection-and-switcher]] intent — proactively
 * offer the visitor their device language on first touch — WITHOUT the SEO
 * anti-pattern PR #72 rightly removed. Crucial invariants:
 *
 *   - NEVER auto-redirects. The canonical (en) page renders fully and stays
 *     crawlable; this is a client-only progressive enhancement that only ever
 *     navigates on an explicit tap (`useNavigate`, no `window.location`).
 *   - Renders after first paint (deferred behind a mount effect) so it stays off
 *     the critical path and doesn't hurt LCP.
 *   - Shows only on the bare/default-locale path, only when the device best-
 *     matches a supported NON-default locale, and only until dismissed/acted —
 *     see `suggestedLocale()` for the exact gate.
 *
 * The CTA + dismiss copy are read from the TARGET locale's catalog via
 * `stringInLocale()`, so a Malay speaker sees "Lihat halaman ini dalam Bahasa
 * Malaysia" even though the surrounding page is still English.
 *
 * Kilat-branded surface, so brand tokens are fair game; no hex/font literals —
 * the primary tint comes from `color-mix()` over the design-token colours.
 */

const DISMISS_KEY = 'kilat:marketing:locale-suggestion-dismissed';

function readDismissed(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* storage blocked (private mode) — the in-memory flag still hides it this session. */
  }
}

function readNavigatorLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    return navigator.languages;
  }
  return navigator.language ? [navigator.language] : [];
}

export function LocaleSuggestionBanner() {
  const brand = usePlatformBrand();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Defer everything to after first paint: read the persisted flag + flip
  // `ready`. Until then the banner renders nothing, so it never participates in
  // the LCP/critical-path render (SEO-safe progressive enhancement).
  useEffect(() => {
    setDismissed(readDismissed());
    setReady(true);
  }, []);

  const target = ready
    ? suggestedLocale({
        pathname,
        languages: readNavigatorLanguages(),
        supported: brand.supportedLocales,
        defaultLocale: brand.defaultLocale,
        dismissed,
      })
    : null;
  const shown = target !== null;

  const dismiss = useCallback(() => {
    persistDismissed();
    setDismissed(true);
  }, []);

  // Esc dismisses while the banner is showing (keyboard parity with the × ).
  useEffect(() => {
    if (!shown) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shown, dismiss]);

  if (!shown || !target) return null;

  const cta = stringInLocale(target, 'marketing.localeSuggestion.cta');
  const dismissLabel = stringInLocale(target, 'marketing.localeSuggestion.dismiss');
  const targetPath = switchPathnameLocale(
    pathname,
    target,
    brand.supportedLocales,
    brand.defaultLocale,
  );

  // Acting counts as "already acted": persist AND flip the in-memory flag so a
  // later return to the bare path (e.g. switching back to English) doesn't
  // re-nag, even if this component instance is reused across the navigation. The
  // navigation itself also moves off the bare path, which hides it immediately.
  function act() {
    persistDismissed();
    setDismissed(true);
    navigate(targetPath);
  }

  return (
    <div
      role="region"
      aria-label={cta}
      lang={target}
      style={{
        width: '100%',
        background: 'color-mix(in srgb, var(--color-primary) 8%, var(--color-surface))',
        borderBottom: '1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)',
        animation: 'kilat-fade-in var(--duration-2) var(--ease-gentle)',
      }}
    >
      <div
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          padding: 'var(--space-2) var(--space-4)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          minWidth: 0,
        }}
      >
        <button
          type="button"
          onClick={act}
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 'var(--touch-target, 44px)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5em',
            padding: '0.375em 0.5em',
            background: 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-primary)',
            fontFamily: 'inherit',
            fontSize: 'var(--text-meta)',
            fontWeight: 600,
            lineHeight: 1.35,
            textAlign: 'left',
            cursor: 'pointer',
            overflowWrap: 'anywhere',
          }}
        >
          <GlobeIcon />
          <span>{cta}</span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={dismissLabel}
          title={dismissLabel}
          style={{
            flexShrink: 0,
            minWidth: 'var(--touch-target, 44px)',
            minHeight: 'var(--touch-target, 44px)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            background: 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-on-surface-muted)',
            cursor: 'pointer',
          }}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}

function GlobeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" fill="none" />
      <path
        d="M3 12 H21 M12 3 C15 6 15 18 12 21 C9 18 9 6 12 3"
        stroke="currentColor"
        strokeWidth="1.75"
        fill="none"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        d="M6 6 L18 18 M18 6 L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
